import React, { useState, useEffect, useRef } from 'react';
import {
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box
} from '@mui/material';
import MyDatePicker from '../../common/components/MyDatePicker';
import { gql, useMutation } from '@apollo/client';
import Swal from 'sweetalert2';
import clientGraphql from '../../../common/apollo-graphql';
import CustomDialog from '../../common/components/CustomDialog';
import ClinicDropdown from '../../common/components/Clinics/ClinicDropdown';
import ProviderDropdown from '../../common/components/Providers/ProviderDropdown';
import Referral from '../../referral/models/Referral';

const UpdateMiniReferralDialog = ({ open, onClose, referral, isPublicPatient = false, isReceived=false }) => {
  const [mrn, setMrn] = useState('');
  const [birthdate, setBirthdate] = useState('');
  const [diagnosis, setDiagnosis] = useState('');
  const [clinic_receiver_id, setClinicReceiverId] = useState('');
  const [provider_id, setProviderId] = useState(0);
  const [clinic_referrer_id, setClinicReferrerId] = useState(0);
  const referralRef = useRef(null);

  const SAVE_REFERRAL = gql`
    mutation SaveReferral($params: String!) {
      save_referral(params: $params)
    }
  `;

  const [saveReferral] = useMutation(SAVE_REFERRAL, {
    client: clientGraphql
  });

  const handleSave = async () => {
    let referral = referralRef.current;
    try {
      const payload = {
        uuid: referral?.referral?.id,
        is_public_patient: isPublicPatient,
        ...(mrn && { mrn }),
        ...(birthdate && { birthdate }),
        ...(diagnosis && { diagnosis }),
          ...(clinic_receiver_id && { clinic_receiver_id }),
          ...(provider_id && { provider_id }),
          ...(clinic_referrer_id && { clinic_referrer_id }),
      };

      await saveReferral({
        variables: {
          params: JSON.stringify(payload),
        },
      });

      Swal.fire({
        icon: 'success',
        text: 'Referral saved!',
        showConfirmButton: true,
      });

      onClose();
    } catch (error) {
      Swal.fire({
        icon: 'error',
        text: error.message || 'Error saving referral!',
        showConfirmButton: true,
      });
    }
  };

  useEffect(() => {
    if (referral) {
      referralRef.current = referral instanceof Referral ? referral : new Referral(referral);

      setMrn(referralRef.current.getMrn() || '');
      setBirthdate(referralRef.current.getPatientDob() || '');
      setDiagnosis(referralRef.current.getDiagnosis() || '');
      setProviderId(referralRef.current.getReceiverProviderId() || '');
      setClinicReceiverId(referralRef.current.getReceiverClinicId() || '');
      setClinicReferrerId(referralRef.current.getReferrerClinicId() || '');
    }
  }, [referral]);

  return (
    <CustomDialog open={open} onClose={onClose}>
      <DialogTitle>Update Referral Information</DialogTitle>
      <DialogContent>
        <Box display="flex" flexDirection="column" gap={3} marginTop={3}>
          <TextField
            label="MRN"
            value={mrn}
            onChange={(e) => setMrn(e.target.value)}
            fullWidth
          />
          <MyDatePicker
            label="Birth Date"
            value={birthdate}
            onChange={(newValue) =>
              setBirthdate(newValue ? newValue.format('YYYY-MM-DD') : null)
            }
          />
          <TextField
            label="Diagnosis"
            value={diagnosis}
            onChange={(e) => setDiagnosis(e.target.value)}
            fullWidth
            multiline
          />

          {isPublicPatient && !isReceived && (
            <ClinicDropdown
              label="Clinic Receiver"
              value={clinic_receiver_id}
              forFax={true}
              onChange={(e) => { 
                  setClinicReceiverId(e.target.value);
                }
              }
            />
          )}

          {isPublicPatient && isReceived && (
            <ClinicDropdown
              label="Clinic Sender"
              value={clinic_referrer_id}
              forFax={true}
              onChange={(e) => { 
                  setClinicReferrerId(e.target.value);
                }
              }
            />
          )}

          <ProviderDropdown
            clinic={clinic_receiver_id ? { id: clinic_receiver_id } : null }
            isReceiver={isReceived}
            label="Provider Receiver"
            value={provider_id}
            forFax={true}
            onChange={(e) => {
               setProviderId(e.target.value);
            }}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="inherit">Cancel</Button>
        <Button onClick={handleSave} variant="contained" color="primary">Save</Button>
      </DialogActions>
    </CustomDialog>
  );
};

export default UpdateMiniReferralDialog;
